import { useMemo, useState } from "react";
import type { DocMeta } from "../../lib/docs";
import { aandachtspunten } from "../../lib/data";
import { Icon } from "../Icon";

type Mode = "highlights" | "browse" | "origineel";

export function DocReader({
  doc,
  onBack,
}: {
  doc: DocMeta;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<Mode>("highlights");
  const [q, setQ] = useState("");

  const highlights = useMemo(
    () =>
      aandachtspunten.filter((a) => a.source.label === doc.highlightSourceLabel),
    [doc],
  );

  const filteredSections = useMemo(() => {
    if (!doc.extracted) return [];
    const term = q.trim().toLowerCase();
    if (!term) return doc.extracted.sections;
    return doc.extracted.sections
      .map((s) => ({
        ...s,
        paragraphs: s.paragraphs.filter((p) =>
          p.text.toLowerCase().includes(term),
        ),
      }))
      .filter((s) => s.paragraphs.length > 0 || s.title.toLowerCase().includes(term));
  }, [doc, q]);

  return (
    <div>
      {/* Header with back */}
      <div className="flex items-start gap-2 mb-3">
        <button
          onClick={onBack}
          aria-label="Terug naar documenten"
          className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 active:bg-ink-700 hover:text-zinc-100"
        >
          ‹
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-100 text-base truncate">
            {doc.title}
          </div>
          <div className="text-2xs text-zinc-500 mt-0.5 truncate">
            {doc.subtitle}
          </div>
          <div className="text-2xs text-zinc-600 mt-0.5 tabular">
            {doc.pages}p · {doc.sizeMb} MB · bron:{" "}
            {doc.extracted?.source_filename ?? doc.filePath.split("/").pop()}
          </div>
        </div>
      </div>

      {/* Mode tabs */}
      <div role="tablist" className="flex gap-1 mb-3 text-xs">
        <ModeTab
          label="Highlights"
          count={highlights.length}
          active={mode === "highlights"}
          onClick={() => setMode("highlights")}
        />
        {doc.extracted && (
          <ModeTab
            label="Doorzoek"
            count={doc.extracted.section_count}
            active={mode === "browse"}
            onClick={() => setMode("browse")}
          />
        )}
        <ModeTab
          label="Origineel"
          active={mode === "origineel"}
          onClick={() => setMode("origineel")}
        />
      </div>

      {/* Content per mode */}
      {mode === "highlights" && (
        <HighlightsList highlights={highlights} />
      )}

      {mode === "browse" && doc.extracted && (
        <>
          <div className="relative mb-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
              <Icon.Search className="w-4 h-4" />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Zoek in ${doc.title.toLowerCase()}…`}
              aria-label={`Zoek in ${doc.title}`}
              className="w-full bg-ink-900 border border-white/[0.08] rounded-lg pl-9 pr-9 py-2.5 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-accent/60"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                aria-label="Wis"
                className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-100 active:bg-ink-700 rounded"
              >
                <Icon.Close className="w-4 h-4" />
              </button>
            )}
          </div>
          <SectionsList sections={filteredSections} />
        </>
      )}

      {mode === "origineel" && (
        <OriginalPdf path={doc.filePath} title={doc.title} />
      )}
    </div>
  );
}

function ModeTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-3 py-2 min-h-[36px] rounded-full border transition-colors ${
        active
          ? "bg-accent/15 border-accent/40 text-accent"
          : "border-line text-zinc-400 hover:text-zinc-200 active:bg-ink-700"
      }`}
    >
      {label}
      {count != null && (
        <span className="opacity-60 tabular ml-1.5">{count}</span>
      )}
    </button>
  );
}

function HighlightsList({
  highlights,
}: {
  highlights: typeof aandachtspunten;
}) {
  if (highlights.length === 0) {
    return (
      <div className="text-2xs text-zinc-500 px-1 py-6 text-center">
        Geen aandachtspunten geëxtraheerd uit dit document.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {highlights.map((a) => (
        <li key={a.id} className="card p-3">
          <div className="flex items-start gap-2 mb-1">
            {a.tijd && (
              <span className="text-2xs tabular text-zinc-300 font-mono shrink-0 mt-0.5">
                {a.tijd}
              </span>
            )}
            <div className="text-sm font-medium text-zinc-100 flex-1">
              {a.wat || a.source.label}
            </div>
          </div>
          {a.locatie && (
            <div className="text-2xs text-zinc-500 mb-1.5">{a.locatie}</div>
          )}
          <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
            {a.tekst}
          </div>
          <div className="text-2xs text-zinc-600 mt-2 pt-2 border-t border-line/60">
            {a.source.ref}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SectionsList({
  sections,
}: {
  sections: { id: string; title: string; level: number; page: number; paragraphs: { text: string; page: number }[] }[];
}) {
  if (sections.length === 0) {
    return (
      <div className="text-2xs text-zinc-500 px-1 py-6 text-center">
        Geen secties matchen.
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {sections.map((s) => (
        <li key={s.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xs tabular text-zinc-600 font-mono shrink-0">
              p{s.page}
            </span>
            <h3
              className={`flex-1 text-zinc-100 ${
                s.level === 1 ? "font-semibold text-base" : "font-medium text-sm"
              }`}
            >
              {s.title}
            </h3>
          </div>
          <div className="space-y-2">
            {s.paragraphs.map((p, idx) => (
              <div
                key={idx}
                className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed"
              >
                {p.text}
              </div>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

function OriginalPdf({ path, title }: { path: string; title: string }) {
  return (
    <div>
      <div className="card p-3 mb-3 flex items-center gap-3">
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-accent text-ink-950 font-semibold text-sm active:bg-accent/85"
        >
          Open in nieuw tabblad
        </a>
        <a
          href={path}
          download
          aria-label={`Download ${title}`}
          className="px-3 py-2.5 rounded-lg border border-line text-zinc-300 text-sm hover:bg-ink-700 active:bg-ink-700"
        >
          Download
        </a>
      </div>
      <div className="card p-1 overflow-hidden">
        <object
          data={path}
          type="application/pdf"
          className="w-full h-[70vh] rounded-xl"
          aria-label={`PDF: ${title}`}
        >
          <div className="p-6 text-center">
            <p className="text-zinc-400 text-sm mb-2">
              Inline PDF-weergave niet ondersteund op dit apparaat.
            </p>
            <a
              href={path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent text-sm underline"
            >
              Open in nieuw tabblad
            </a>
          </div>
        </object>
      </div>
    </div>
  );
}
